import { Box, Grid, Badge, Typography } from '@mui/material';
import './portal-home-page.scss';
import { PortalCardMenu } from './components/portal-card-menu/portal-card-menu.tsx';
import { PortalCardLink } from './components/portal-card-link/portal-card-link.tsx';
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded';
import BrushRoundedIcon from '@mui/icons-material/BrushRounded';
import VrpanoRoundedIcon from '@mui/icons-material/VrpanoRounded';
import RecentActorsRoundedIcon from '@mui/icons-material/RecentActorsRounded';
import { useGetDocumentTree } from '../../api/documentHub.ts';
import { getUnreadDocumentCount } from '../../unstructured-pages/DocumentHubPage/tableUtills.tsx';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import FileCopyRoundedIcon from '@mui/icons-material/FileCopyRounded';
import GradingRoundedIcon from '@mui/icons-material/GradingRounded';
import MessageIcon from '@mui/icons-material/Message';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTitle } from '../../hooks/useTitle.ts';
import { useFetchUserAttributes } from '../../hooks/auth.ts';
import { useElementSize } from '../../hooks/use-element-size.ts';
import { HomeMenu } from './components/home-menu/home-menu.tsx';
import { LandMenu } from './components/land-menu/land-menu.tsx';

export const PortalHomeView = () => {
  useTitle('My Portal - Immersive Homes');
  const { data: folders } = useGetDocumentTree();

  const [anchorElHome, setAnchorElHome] = useState<null | HTMLElement>(null);
  const [anchorElLand, setAnchorElLand] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();

  const [isLandMenuOpen, setIsLandMenuOpen] = useState(false);

  const [isHomeMenuOpen, setIsHomeMenuOpen] = useState(false);

  const navFromMenu = (path: string) => {
    void navigate(path);
    setIsHomeMenuOpen(false);
    setIsLandMenuOpen(false);
  };

  const {
    size: { width: homeMenuWidth },
  } = useElementSize({ el: anchorElHome });

  const {
    size: { width: landMenuWidth },
  } = useElementSize({ el: anchorElLand });

  const openHomeMenu = () => {
    setIsHomeMenuOpen(true);
  };

  const openLandMenu = () => {
    setIsLandMenuOpen(true);
  };

  const closeHomeMenu = () => {
    setIsHomeMenuOpen(false);
  };

  const closeLandMenu = () => {
    setIsLandMenuOpen(false);
  };

  const user = useFetchUserAttributes();

  return (
    <Box
      sx={{ display: 'flex' }}
      className="portal-home-page"
      data-testid="portal-home-page"
    >
      <Box className="container">
        <Grid container className="grid" spacing={2}>
          <PortalCardMenu usersName={user?.data?.given_name} />
          <Grid size={12}>
            <Typography className="quick-link">Quick Links</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <Box ref={setAnchorElHome} onClick={openHomeMenu}>
              <PortalCardLink
                title="My Home"
                text="View favorited home models, floor plans, finishing packages and more"
                Icon={HomeRoundedIcon}
              />
            </Box>
            <HomeMenu
              isOpen={isHomeMenuOpen}
              width={homeMenuWidth}
              navFromMenu={navFromMenu}
              anchorEl={anchorElHome}
              close={closeHomeMenu}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <Box sx={{ position: 'relative' }}>
              {folders && (
                <Badge
                  sx={{ position: 'absolute', top: '32px', left: '82px' }}
                  badgeContent={getUnreadDocumentCount(folders)}
                  color="error"
                ></Badge>
              )}
              <PortalCardLink
                title="Document Hub"
                path="/portal/document-hub"
                text="Upload and manage project, financing, contractual and other documentation"
                Icon={FileCopyRoundedIcon}
              />
            </Box>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <Box ref={setAnchorElLand} onClick={openLandMenu}>
              <PortalCardLink
                title="My Land"
                text="Explore prospective land, view saved searches, favorited lots, and more"
                Icon={VrpanoRoundedIcon}
              />
            </Box>
            <LandMenu
              isOpen={isLandMenuOpen}
              width={landMenuWidth}
              navFromMenu={navFromMenu}
              anchorEl={anchorElLand}
              close={closeLandMenu}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <PortalCardLink
              path="message-center"
              title="Message Center"
              text="View your messages and contact our team"
              Icon={MessageIcon}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <PortalCardLink
              path="/pricing"
              title="Pricing Dashboard"
              text="View your unique pricing dashboard"
              Icon={MonetizationOnRoundedIcon}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <PortalCardLink
              path="/portal/style"
              title="Style Inspiration"
              text="Upload inspiration, share your aesthetic preferences"
              Icon={BrushRoundedIcon}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <PortalCardLink
              title="Contact Information"
              path="/contact-information"
              text="Manage your contact information and methods"
              Icon={RecentActorsRoundedIcon}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <PortalCardLink
              path="project-status"
              title="Project Status"
              text="View the latest milestones for your Home, Land, and Construction"
              Icon={GradingRoundedIcon}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
